<template>
  <div id="mathfield" ref="mathfield"></div>
  <v-dialog v-model="isModalVisible">
    <v-card
      :style="{
        top: modalPosition.top + 'px',
        left: modalPosition.left + 'px',
      }"
      class="w-25"
    >
      <v-card-title class="bg-secondary ma-0">{{
        $t("labels.selectOption")
      }}</v-card-title>
      <v-list>
        <v-list-item @click="selectOption('\\max')"
          >Max (<strong class="text-primary">max</strong>)</v-list-item
        >
        <v-list-item @click="selectOption('\\min')"
          >Min (<strong class="text-primary">min</strong>)
        </v-list-item>
        <v-list-item @click="selectOption('med')"
          >{{ $t("labels.median") }} (
          <strong class="text-primary">med</strong>
          )</v-list-item
        >
        <v-list-item @click="selectOption('\\mu')"
          >{{ $t("labels.mean") }} (
          <strong class="text-primary">&mu;</strong>
          )
        </v-list-item>
        <v-list-item @click="selectOption('Mo')"
          >{{ $t("labels.mode") }} (<strong class="text-primary">Mo</strong>)
        </v-list-item>
        <v-list-item @click="selectOption('\\sigma')"
          >{{ $t("labels.stddev") }} (
          <strong class="text-primary">&sigma;</strong>
          )</v-list-item
        >
        <v-list-item @click="selectOption('&Sigma;')"
          >{{ $t("labels.sum") }} (<strong class="text-primary">&Sigma;</strong
          >)</v-list-item
        >
      </v-list>
      <v-card-actions>
        <v-btn
          variant="outlined"
          color="secondary"
          @click="isModalVisible = false"
          >{{ $t("labels.cancel") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { MathfieldElement } from "mathlive";

export default {
  name: "MathField",
  props: ["valueFormula", "locale"],
  emits: ["writeFormula"],
  data() {
    return {
      mathfield: null,
      formulaValue: this.valueFormula,
      isModalVisible: false,
      modalPosition: { top: 0, left: 0 },
      content: "",
    };
  },
  mounted() {
    if (typeof MathfieldElement !== "undefined") {
      this.initializeMathfield();
    } else {
      console.error(
        "MathfieldElement is not defined. Please check your imports.",
      );
    }
  },
  beforeUnmount() {
    if (this.mathfield && this.$refs.mathfield.contains(this.mathfield)) {
      this.$refs.mathfield.removeChild(this.mathfield);
    }
  },
  methods: {
    initializeMathfield() {
      MathfieldElement.locale = this.locale;
      this.mathfield = document.createElement("math-field");
      MathfieldElement.soundsDirectory = null;
      MathfieldElement.fontsDirectory = "";
      console.log("Initializing Mathfield with locale:", this.locale);

      // Disable the virtual keyboard
      this.mathfield.setAttribute("virtual-keyboard-mode", "off");
      this.mathfield.virtualKeyboardMode = "off";

      this.$nextTick(() => {
        this.mathfield.value = this.valueFormula;
        this.mathfield.menuItems = [];

        // Remove the menu button after the mathfield is rendered
        const observer = new MutationObserver(() => {
          const menuToggle =
            this.mathfield.shadowRoot?.querySelector(".ML__menu-toggle");
          if (menuToggle) {
            menuToggle.remove();
            observer.disconnect();
          }
        });

        observer.observe(this.mathfield.shadowRoot, {
          childList: true,
          subtree: true,
        });
      });

      this.mathfield.addEventListener("input", () => {
        this.formulaValue = this.mathfield.value;
      });

      this.$refs.mathfield.appendChild(this.mathfield);
      document.documentElement.style.setProperty("--keyboard-zindex", "3000");

      // Custom drag & drop handling
      this.mathfield.addEventListener("drop", this.handleDrop);
      this.mathfield.addEventListener("dragover", (event) => {
        event.preventDefault();
      });

      // Update the virtual keyboard tooltip (if present)
      this.updateKeyboardTooltip();
    },
    updateKeyboardTooltip() {
      // Set a custom tooltip on the virtual keyboard button
      const keyboardToggle = this.mathfield.shadowRoot?.querySelector(
        ".ML__virtual-keyboard-toggle",
      );
      if (keyboardToggle) {
        const tooltipText = this.$t("labels.toggleKeyboard");
        keyboardToggle.setAttribute("data-tooltip", tooltipText);
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.modalPosition.left = event.clientX;
      if (
        this.modalPosition.left + (this.modalPosition.left / 100) * 25 >
        window.innerWidth - 150
      ) {
        this.modalPosition.left =
          window.innerWidth -
          ((this.modalPosition.left / 100) * 25 + window.innerWidth / 10);
      }
      this.modalPosition.top =
        ((window.innerHeight / event.clientY) * 80 - 200) * -1;
      const data = event.dataTransfer.getData("text");

      this.showModalAndWaitForSelection().then((selectedOption) => {
        this.mathfield.insert(this.latexComposition(selectedOption, data));
      });
    },
    latexComposition(operator, data) {
      return operator + `\\left(${data.trim().replace(/\s/g, "")}\\right)`;
    },
    selectOption(option) {
      this.content = option;
      this.isModalVisible = false;
      if (this.resolveModal) {
        this.resolveModal(option);
      }
    },
    showModalAndWaitForSelection() {
      return new Promise((resolve) => {
        this.isModalVisible = true;
        this.resolveModal = resolve;
      });
    },
  },
  watch: {
    value(newVal) {
      if (newVal !== this.formulaValue) {
        this.formulaValue = newVal;
      }
    },
    formulaValue(newVal) {
      this.$emit("writeFormula", newVal);
    },
    locale(newLocale) {
      if (this.mathfield && this.$refs.mathfield.contains(this.mathfield)) {
        this.$refs.mathfield.removeChild(this.mathfield);
        this.mathfield = null;
      }
      MathfieldElement.locale = newLocale;
      this.initializeMathfield();
    },
  },
};
</script>

<style scoped>
#mathfield :deep(math-field) {
  width: 100%;
  position: relative;
  font-size: 18px;
  z-index: 1;
  border-radius: 4px;
  padding: 4px;
}
#mathfield :deep(math-field::part(virtual-keyboard-toggle)) {
  position: relative;
}

#mathfield :deep(math-field::part(virtual-keyboard-toggle)::after) {
  content: attr(data-tooltip);
  position: absolute;
  right: 100%;
  margin-right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 9999;
}
</style>
